import React, { useState, useEffect, useRef } from "react";
import {
  TextField,
  MenuItem,
  Button,
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { getSettingsRequest } from "../../../redux/settings/settingsSlice";
import {
  fetchContentRequest,
  saveContentRequest,
} from "../../../redux/Content/ContentSlice";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import { canShowPermission } from "../Permissions/permissionObject";

const CustomIconButton = ({ children, ...props }) => (
  <Button
    variant="outlined"
    size="small"
    {...props}
    sx={{ minWidth: "36px", p: 0.5 }}
  >
    {children}
  </Button>
);

const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  return (
    <div
      style={{ display: "flex", flexWrap: "wrap", gap: "8px", padding: "16px" }}
    >
      <CustomIconButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        color={editor.isActive("bold") ? "primary" : "inherit"}
      >
        <i className="ri-bold" />
      </CustomIconButton>
      <CustomIconButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        color={editor.isActive("italic") ? "primary" : "inherit"}
      >
        <i className="ri-italic" />
      </CustomIconButton>
      <CustomIconButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        color={editor.isActive("underline") ? "primary" : "inherit"}
      >
        <i className="ri-underline" />
      </CustomIconButton>
      <CustomIconButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        color={editor.isActive("strike") ? "primary" : "inherit"}
      >
        <i className="ri-strikethrough" />
      </CustomIconButton>
      <CustomIconButton
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
        color={editor.isActive({ textAlign: "left" }) ? "primary" : "inherit"}
      >
        <i className="ri-align-left" />
      </CustomIconButton>
      <CustomIconButton
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
        color={editor.isActive({ textAlign: "center" }) ? "primary" : "inherit"}
      >
        <i className="ri-align-center" />
      </CustomIconButton>
      <CustomIconButton
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
        color={editor.isActive({ textAlign: "right" }) ? "primary" : "inherit"}
      >
        <i className="ri-align-right" />
      </CustomIconButton>
      <CustomIconButton
        onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        color={
          editor.isActive({ textAlign: "justify" }) ? "primary" : "inherit"
        }
      >
        <i className="ri-align-justify" />
      </CustomIconButton>
    </div>
  );
};

const Content = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.settingData);
  const contentData = useSelector((state) => state?.content?.contentData);

  const [contentOptions, setContentOptions] = useState([]);
  const [selectedContent, setSelectedContent] = useState("");

  const hasFetched = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder: "Write something here..." }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: "",
  });

  useEffect(() => {
    dispatch(getSettingsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (data?.data?.contents?.length) {
      setContentOptions(data.data.contents);
      setSelectedContent(data.data.contents[0]);
    }
  }, [data]);

  useEffect(() => {
    if (selectedContent) {
      dispatch(
        fetchContentRequest({ select_content_to_edit: selectedContent })
      );
    }
  }, [dispatch, selectedContent]);

  useEffect(() => {
    if (!editor) return;

    if (contentData?.data) {
      editor.commands.setContent(contentData?.data, "html");
    } else {
      editor.commands.setContent("");
    }
  }, [contentData, editor]);

  const handleSave = () => {
    dispatch(
      saveContentRequest({
        select_content_to_edit: selectedContent,
        content: JSON.stringify(editor?.getHTML()),
      })
    );
  };

  const [permissions, setPermissions] = useState({});
    useEffect(() => {
        const storedPermissions = localStorage.getItem("userPermissions");
        if (storedPermissions) {
          try {
            setPermissions(JSON.parse(storedPermissions));
          } catch (error) {
            console.error("Error parsing stored permissions", error);
          }
        }
      }, []);
    const canCreateContent = canShowPermission(permissions, "content", "create");
    const canEdit = canShowPermission(permissions, "content", "edit");

  return (
    <Box
      sx={{
        width: "1243px",
        p: { xs: 2, sm: 3 },
        overflow: "auto",
        height: "100vh",
        mt: 0,
      }}
    >
      <Card sx={{ p: 2, maxWidth: "1143px", borderRadius: "16px", mx: "auto" }}>
        <Typography fontWeight={700} fontSize={24} mb={3} fontFamily={"Aeonik"}>
          Content Editor
        </Typography>

        <CardContent
          sx={{
            backgroundColor: "white",
            borderRadius: "16px",
            border: "1px solid #e0e0e0",
          }}
        >
          <TextField
            select
            label="Select Content To Edit"
            value={selectedContent}
            onChange={(e) => setSelectedContent(e.target.value)}
            fullWidth
            margin="normal"
          >
            {contentOptions.map((title) => (
              <MenuItem key={title} value={title}>
                {title}
              </MenuItem>
            ))}
          </TextField>

          {editor && (
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                mb: 2,
                "& .ProseMirror": {
                  minHeight: "300px",
                  padding: "12px",
                  outline: "none",
                  whiteSpace: "pre-wrap",
                },
              }}
            >
              <EditorToolbar editor={editor} />
              <Divider />
              <EditorContent
                editor={editor}
                style={{
                  minHeight: "300px",
                  padding: "12px",
                  outline: "none",
                  cursor: "text",
                  whiteSpace: "pre-wrap",
                }}
              />
            </Box>
          )}

        {(canCreateContent || canEdit) && ( <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              backgroundColor: "#752ADA",
              px: 3,
              fontSize: "14px",
              textTransform: "none",
              height: "40px",
              mt: 2,
            }}
          >
            Save
          </Button>)} 
        </CardContent>
      </Card>
    </Box>
  );
};

export default Content;
